import { c as _c } from "react/compiler-runtime";
import { useContext, useEffect, useMemo, useRef } from 'react';
import { useFormikContext } from 'formik';
import { useTheme } from 'styled-components';
import useSWR from 'swr';
import APIMethodKeys from '../../../client/APIMethodKeys';
import { GlobalContext } from '../../contexts/GlobalContext';
import MultiSelectInput from '../Form/Fields/MultiSelectInput';
import RadioCardsInput from '../Form/Fields/RadioCardsInput';
import SelectResourceInput from '../Form/Fields/SelectResourceInput';
import Badge from '../base/Badge';
import Text from '../base/Text';
import { Div } from '../helpers/StyledUtils';
export interface ReplayTargetFormValues {
  source_id: string;
  connection_scope: 'all' | 'specific';
  webhook_ids: string[];
}

/**
 * Shared Formik validation for the replay target fields. Callers merge the
 * result into their own form errors.
 */
export const validateReplayTarget = ({
  source_id,
  connection_scope,
  webhook_ids
}: ReplayTargetFormValues): {
  source_id?: string;
  webhook_ids?: string;
} => {
  const errors: {
    source_id?: string;
    webhook_ids?: string;
  } = {};
  if (!source_id) {
    errors.source_id = 'Select a target source';
  }
  if (connection_scope === 'specific' && (!webhook_ids || webhook_ids.length === 0)) {
    errors.webhook_ids = 'Select at least one target connection';
  }
  return errors;
};

/**
 * Builds the API `target` payload from the form values: a source-only target
 * replays onto all of the source's active connections (resolved at replay
 * time), while the specific scope pins the selected connections.
 */
export const formatReplayTarget = ({
  source_id,
  connection_scope,
  webhook_ids
}: ReplayTargetFormValues): {
  source_id: string;
  webhook_ids?: string[];
} => connection_scope === 'specific' ? {
  source_id,
  webhook_ids
} : {
  source_id
};

/**
 * Connection picker scoped to the selected target source. Lives in its own
 * component so the pre-seeding SWR fetch re-runs when the source changes.
 */
const TargetConnectionsField: React.FC<{
  source_id: string;
}> = t0 => {
  const $ = _c(14);
  const {
    source_id
  } = t0;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  let t1;
  if ($[0] !== source_id) {
    t1 = APIMethodKeys.webhooks.list({
      limit: 250,
      source_id
    });
    $[0] = source_id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== HookdeckAPI.webhooks || $[3] !== source_id) {
    t2 = () => HookdeckAPI.webhooks.list({
      limit: 250,
      source_id
    });
    $[2] = HookdeckAPI.webhooks;
    $[3] = source_id;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data: webhooks_data
  } = useSWR(t1, t2);
  let t3;
  if ($[5] !== webhooks_data?.models) {
    t3 = webhooks_data?.models.map(_temp) ?? [];
    $[5] = webhooks_data?.models;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const initial_webhook_options = t3;
  const formatOptions = _temp3;
  const t4 = `${source_id}-${initial_webhook_options.length > 0 ? "seeded" : "empty"}`;
  let t5;
  if ($[7] !== source_id) {
    t5 = (search_term, ids) => ({
      limit: 250,
      source_id,
      full_name: search_term ? search_term : undefined,
      id: ids
    });
    $[7] = source_id;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== HookdeckAPI.webhooks.list || $[10] !== initial_webhook_options || $[11] !== t4 || $[12] !== t5) {
    t6 = <MultiSelectInput key={t4} name="webhook_ids" label="" option_monospace={true} initial_options={initial_webhook_options} fetcher={HookdeckAPI.webhooks.list} fetcherKey={APIMethodKeys.webhooks.list} formatOptions={formatOptions} formatFilters={t5} required={true} />;
    $[9] = HookdeckAPI.webhooks.list;
    $[10] = initial_webhook_options;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
};

/**
 * Replay target selection shared by the bulk replay form and the single
 * request Replay dialog: pick a target source first, then replay onto either
 * all of that source's connections or a specific subset. Rendered as a
 * two-step guide (numbered badges with a connector line, onboarding-style).
 * Expects the host Formik form to carry `ReplayTargetFormValues` (additional
 * fields are fine).
 */
const ReplayTargetFields: React.FC = () => {
  const $ = _c(38);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    values,
    setFieldValue
  } = useFormikContext();
  const theme = useTheme();
  const previous_source_id = useRef(values.source_id);
  let t0;
  let t1;
  if ($[0] !== setFieldValue || $[1] !== values.source_id) {
    t0 = () => {
      if (previous_source_id.current !== values.source_id) {
        previous_source_id.current = values.source_id;
        setFieldValue("webhook_ids", []);
      }
    };
    t1 = [values.source_id, setFieldValue];
    $[0] = setFieldValue;
    $[1] = values.source_id;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      direction: "column",
      align: "center"
    };
    t3 = <Badge neutral={true}>1</Badge>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      grow: true
    };
    t5 = {
      px: 1
    };
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  let t6;
  if ($[8] !== theme.colors.outline.neutral) {
    t6 = <Div flex={t2}>{t3}<Div flex={t4} w={t5} style={{
        backgroundColor: theme.colors.outline.neutral
      }} /></Div>;
    $[8] = theme.colors.outline.neutral;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  let t8;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      grow: true,
      direction: "column"
    };
    t8 = {
      l: 3,
      b: 0
    };
    t9 = {
      minWidth: 0
    };
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
  } else {
    t7 = $[10];
    t8 = $[11];
    t9 = $[12];
  }
  let t10;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Text semi={true} m={{
      b: 1
    }}>Replay target source</Text>;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Text muted={true} size="xs" m={{
      b: 3
    }}>Each matching request is re-ingested as one new request on the target source, with one event per target connection.</Text>;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== HookdeckAPI.sources.list) {
    t12 = <Div flex={t7} p={t8} style={t9}>{t10}{t11}<SelectResourceInput name="source_id" placeholder="Select..." search_placeholder="Find a source" listResources={HookdeckAPI.sources.list} listResourcesKey={APIMethodKeys.sources.list} /></Div>;
    $[15] = HookdeckAPI.sources.list;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== t12 || $[18] !== t6) {
    t13 = <Div flex={true}>{t6}{t12}</Div>;
    $[17] = t12;
    $[18] = t6;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Badge neutral={true}>2</Badge>;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  let t16;
  let t17;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      grow: true,
      direction: "column"
    };
    t16 = {
      l: 3
    };
    t17 = {
      minWidth: 0
    };
    $[21] = t15;
    $[22] = t16;
    $[23] = t17;
  } else {
    t15 = $[21];
    t16 = $[22];
    t17 = $[23];
  }
  let t18;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Text semi={true} m={{
      b: 1
    }}>Replay target connections</Text>;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Text muted={true} size="xs" m={{
      b: 3
    }}>The source’s active connections are resolved as the replay runs, so connections added or removed mid-replay are honored.</Text>;
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  let t20;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      value: "all",
      label: "All connections on this source"
    };
    $[26] = t20;
  } else {
    t20 = $[26];
  }
  const t21 = !values.source_id;
  let t22;
  if ($[27] !== t21) {
    t22 = <RadioCardsInput name="connection_scope" options={[t20, {
      value: "specific",
      label: "Specific connections",
      disabled: t21
    }]} />;
    $[27] = t21;
    $[28] = t22;
  } else {
    t22 = $[28];
  }
  let t23;
  if ($[29] !== values.connection_scope || $[30] !== values.source_id) {
    t23 = values.connection_scope === "specific" && values.source_id && <Div m={{
      t: 4,
      b: -4
    }}><TargetConnectionsField source_id={values.source_id} /></Div>;
    $[29] = values.connection_scope;
    $[30] = values.source_id;
    $[31] = t23;
  } else {
    t23 = $[31];
  }
  let t24;
  if ($[32] !== t22 || $[33] !== t23) {
    t24 = <Div flex={true}>{t14}<Div flex={t15} p={t16} style={t17}>{t18}{t19}{t22}{t23}</Div></Div>;
    $[32] = t22;
    $[33] = t23;
    $[34] = t24;
  } else {
    t24 = $[34];
  }
  let t25;
  if ($[35] !== t13 || $[36] !== t24) {
    t25 = <>{t13}{t24}</>;
    $[35] = t13;
    $[36] = t24;
    $[37] = t25;
  } else {
    t25 = $[37];
  }
  return t25;
};
export default ReplayTargetFields;
function _temp(m) {
  return {
    value: m.id,
    label: m.full_name
  };
}
function _temp2(m_0) {
  return {
    value: m_0.id,
    label: m_0.full_name
  };
}
function _temp3(data) {
  return data && data.models.map(_temp2);
}